import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Stack, Typography} from 'views/components';
import {TaskEditor, Translate, translate} from 'views/module';

import TaskItem from './components/TaskItem';
import TaskListSkeleton from './components/TaskListSkeleton';

import backend from 'utils/backend';
import moment from 'moment';

const MyDay = (props) => {
  const {
    user,
    taskRefreshSetMyTasksCount,
    statusProgressWrap: progressWrap,
  } = props;

  const [todaysTasks, setTodaysTasks] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editorSection, setEditorSection] = React.useState(null);
  const [editingTaskRn, setEditingTaskRn] = React.useState(null);

  const loadTasks = React.useCallback(() => {
    if (!user?.userRn) {
      setTodaysTasks([]);
      return;
    }

    setIsLoading(true);

    progressWrap(async () => {
      try {
        const oneWeekAgoUtc = moment().startOf('day').subtract(1, 'week').utc();
        const endOfTodayUtc = moment().endOf('day').utc();
        const startDate = Math.floor(new Date(oneWeekAgoUtc.toISOString()).getTime());
        const endOfTodayDate = Math.floor(new Date(endOfTodayUtc.toISOString()).getTime());
        const taskSortBy = {sortKey: 'dueDate', sortDirection: 'ASC'};
        const userTaskFilters = [
          {key: 'resourceRn', value: user.userRn},
          {key: 'dueDate', value: `[${startDate} TO ${endOfTodayDate}]`},
          {key: 'status', value: ['OPEN', 'COMPLETE']},
        ];
        let allUserTasks = [];
        let lastEvaluatedKey = null;

        while (lastEvaluatedKey !== undefined) {
          const userTaskResponse = await backend.task.queryResourceTasks(userTaskFilters, taskSortBy, lastEvaluatedKey);
          allUserTasks = [...allUserTasks, ...(userTaskResponse?.list || [])];
          lastEvaluatedKey = userTaskResponse?.lastEvaluatedKey;
          if (!lastEvaluatedKey) {
            break;
          }
        }
        setTodaysTasks(allUserTasks);
      } finally {
        setIsLoading(false);
      }
    });
  }, [progressWrap, user?.userRn]);

  const hydrateTaskAssignees = React.useCallback(async (taskData) => {
    const connectionRns = taskData.connections || [];
    const ownerRns = taskData.ownerRns || [];

    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
        try {
          const clientResponse = await backend.client.getClient(connectionRn);
          const clientDetail = clientResponse.clientDetail;
          const [firstName, ...lastNameParts] = clientDetail?.clientName.split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
        try {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn);
          const caregiverDetail = caregiverResponse.caregiverDetail;
          const caregiverName = caregiverDetail.caregiverName;
          const [firstName, ...lastNameParts] = caregiverName.trim().split(' ');

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),
          };
        } catch {
          return null;
        }
      }

      return null;
    }));

    const ownerInfo = await Promise.all(ownerRns.map(async (ownerRn) => {
      try {
        const adminResponse = await backend.user.getUser(ownerRn);

        return {
          ownerRn,
          firstName: adminResponse.user.firstName,
          lastName: adminResponse.user.lastName,
        };
      } catch {
        return null;
      }
    }));

    return {
      ...taskData,
      connectionInfo: connectionInfo.filter(Boolean),
      ownerInfo: ownerInfo.filter(Boolean),
    };
  }, []);

  const handleToggleTaskStatus = React.useCallback((taskRn, nextStatus) => {
    progressWrap(async () => {
      const updatedTaskResponse = await backend.task.updateTaskStatus(taskRn, nextStatus);
      const updatedTask = updatedTaskResponse.task;

      setTodaysTasks((previousTasks) =>
        previousTasks.map((task) => task.taskRn === taskRn ? updatedTask : task),
      );
    });
  }, [progressWrap]);

  React.useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const {arrangedTasks, myTasksCount} = React.useMemo(() => {
    const visibleTasks = todaysTasks.filter((task) =>
      !(task.status === 'COMPLETE' && moment(task.dueDate).isBefore(moment().startOf('day').utc())),
    );
    const incompleteUserTasks = visibleTasks.filter((task) =>
      task.status !== 'COMPLETE',
    );
    const visibleCompletedUserTasks = visibleTasks.filter((task) =>
      task.status === 'COMPLETE',
    );

    return {
      arrangedTasks: [...incompleteUserTasks, ...visibleCompletedUserTasks],
      myTasksCount: incompleteUserTasks.length,
    };
  }, [todaysTasks]);

  React.useEffect(() => {
    taskRefreshSetMyTasksCount(myTasksCount);
  }, [myTasksCount, taskRefreshSetMyTasksCount]);

  const openDialogs = {
    addTask: (section) => {
      setEditingTaskRn(null);
      setIsEditorOpen(true);
      setEditorSection(section);
    },
    editTask: (task) => {
      setIsEditorOpen(false);
      setEditorSection(null);
      setEditingTaskRn(task.taskRn);
    },
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditorSection(null);
    setEditingTaskRn(null);
  };
  const edits = {
    addTask: (taskData) => {
      progressWrap(async () => {
        try {
          const createdTaskResponse = await backend.task.addTask(taskData);
          const createdTask = createdTaskResponse.task;
          const ownerRns = createdTask.ownerRns || [];
          const isAssignedToCurrentUser = ownerRns.includes(user.userRn);
          const isDueToday = moment(createdTask?.dueDate).isBetween(
              moment().startOf('day'),
              moment().endOf('day'),
              null,
              '[]',
          );

          if (isAssignedToCurrentUser && isDueToday) {
            const taskWithInfo = await hydrateTaskAssignees(createdTask);

            setTodaysTasks((previousTasks) =>
              [...previousTasks, taskWithInfo],
            );
          }
        } finally {
          closeEditor();
        }
      });
    },
    editTask: (taskRn, taskData) => {
      progressWrap(async () => {
        try {
          const updatedTaskResponse = await backend.task.updateTask(taskRn, taskData);
          const updatedTask = updatedTaskResponse.task;
          const ownerRns = updatedTask.ownerRns || [];
          const isAssignedToCurrentUser = ownerRns.includes(user.userRn);
          const isDueToday = moment(updatedTask?.dueDate).isBetween(
              moment().startOf('day'),
              moment().endOf('day'),
              null,
              '[]',
          );

          if (isAssignedToCurrentUser && isDueToday) {
            const taskWithInfo = await hydrateTaskAssignees(updatedTask);
            setTodaysTasks((previousTasks) =>
              previousTasks.map((task) => task.taskRn === taskRn ? taskWithInfo : task),
            );
          } else {
            setTodaysTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskRn));
          }
        } finally {
          closeEditor();
        }
      });
    },
    removeTask: (taskData) => {
      setTodaysTasks((previousTasks) => previousTasks.filter((task) => task.taskRn !== taskData.taskRn));

      progressWrap(async () => {
        try {
          await backend.task.removeTask(taskData.taskRn);
        } finally {
          setEditingTaskRn(null);
        }
      });
    },
  };

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        pt: 4,
        pb: 20,
        maxWidth: 960,
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
        }}
      >
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 400,
            }}
          >
            {translate('tasks.MyDay.Header.Greeting', {userFirstName: user.firstName})}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
          >
            {translate('tasks.MyDay.Header.Summary', {todayDate: moment().format('dddd MMM DD'), numTasks: myTasksCount})}
          </Typography>
        </Stack>
        {
          !isEditorOpen && !editingTaskRn && (
            <Button
              variant='contained'
              onClick={() => openDialogs.addTask('HEADER')}
              sx={{
                backgroundColor: '#DA7955',
                color: '#FFFFFF',
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              <Box component='span' mr='4px'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Button>
          )
        }
      </Stack>
      {
        isEditorOpen && editorSection === 'HEADER' && !editingTaskRn && (
          <TaskEditor
            task={{
              ownerInfo: [{
                ownerRn: user.userRn,
                firstName: user.firstName,
                lastName: user.lastName,
              }],
            }}
            onCancel={closeEditor}
            onSubmit={edits.addTask}
          />
        )
      }
      <Stack
        spacing={1.5}
      >
        <Typography
          variant="overline"
          sx={{
            letterSpacing: '0.12em',
            fontSize: '16px',
            fontWeight: 700,
            color: 'grey.500',
          }}
        >
          {translate('tasks.MyDay.Sections.NeedsYou')}
        </Typography>
        <Stack
          spacing={1.5}
        >
          {
            isLoading && (
              <TaskListSkeleton />
            )
          }
          {
            !isLoading && arrangedTasks.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {translate('tasks.MyDay.Empty')}
              </Typography>
            )
          }
          {
            !isLoading && arrangedTasks.map((task) => (
              editingTaskRn === task.taskRn ?
                <TaskEditor
                  key={task.taskRn}
                  isEditing
                  task={task}
                  onCancel={closeEditor}
                  onSubmit={(payload) => edits.editTask(task.taskRn, payload)}
                /> :
                <TaskItem
                  key={task.taskRn}
                  task={task}
                  onToggleTaskStatus={handleToggleTaskStatus}
                  onClickEditTask={openDialogs.editTask}
                  removeTask={edits.removeTask}
                />
            ))
          }
          {
            isEditorOpen && editorSection === 'BOTTOM' && !editingTaskRn && (
              <TaskEditor
                task={{
                  ownerInfo: [{
                    ownerRn: user.userRn,
                    firstName: user.firstName,
                    lastName: user.lastName,
                  }],
                }}
                onCancel={closeEditor}
                onSubmit={edits.addTask}
              />
            )
          }
          {
            !isEditorOpen && !editingTaskRn && (
              <Box
                component='button'
                onClick={() => openDialogs.addTask('BOTTOM')}
                sx={{
                  'background': 'none',
                  'border': 'none',
                  'color': '#A8A49E',
                  'cursor': 'pointer',
                  'display': 'inline-flex',
                  'alignItems': 'center',
                  'gap': '4px',
                  'fontSize': '14px',
                  'p': 0,
                  'mt': 1,
                  'transition': 'color 120ms ease',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                <Box component='span'>+</Box>
                <Translate expression='tasks.Common.NewTask' />
              </Box>
            )
          }
        </Stack>
      </Stack>
    </Stack>
  );
};

MyDay.propTypes = {
  user: PropTypes.object,
  taskRefreshSetMyTasksCount: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(MyDay));
